import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Arrow, Directions } from 'components/Shared/Arrow';

export default function CarouselButtons({
  next,
  previous,
  slideIndex,
  setSlideIndex,
  onSlideChange,
  images,
  goToSlide,
  lightboxIndex,
  setLightboxIndex,
  setSlidePage,
  isTransitioning,
}: {
  next: () => void;
  previous: () => void;
  slideIndex: number;
  setSlideIndex: React.Dispatch<React.SetStateAction<number>>;
  onSlideChange: (index: number) => void;
  goToSlide: (index: number) => void;
  setSlidePage: (index: number) => void;
  images: string[];
  lightboxIndex: number | null;
  setLightboxIndex: (num: number | null) => void;
  isTransitioning: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  // if the lightbox is closed, we set the index it was closed at in the VDP
  // set the offset index in the carousel
  // determine whether index falls on a new page, then set the new page and reset lightox index
  // useEffect is set here because only CarouselButton is passed goToSlide by react-multi-carousel
  useEffect(() => {
    if (lightboxIndex !== null) {
      goToSlide(lightboxIndex + 2);
      const page =
        (lightboxIndex + 1) % 4 === 0
          ? Math.max(Math.floor((lightboxIndex + 1) / 4) - 1, 0)
          : Math.floor((lightboxIndex + 1) / 4);
      setSlidePage(page);
      setSlideIndex(lightboxIndex);
      setLightboxIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex]);

  function onLeftClick(): void {
    if (isTransitioning) return;
    if (slideIndex === 0) {
      setSlideIndex(images.length - 1);
      onSlideChange(images.length - 1);
    } else {
      setSlideIndex((val: number) => val - 1);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      onSlideChange(slideIndex - 1);
    }
    previous();
  }

  function onRightClick(): void {
    if (isTransitioning) return;
    if (slideIndex === images.length - 1) {
      setSlideIndex(0);
      onSlideChange(0);
    } else {
      setSlideIndex((val: number) => val + 1);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      onSlideChange(slideIndex + 1);
    }
    next();
  }

  return (
    <>
      <div>
        <Arrow
          direction={Directions.Left}
          onClick={onLeftClick}
          style={{
            position: 'absolute',
            left: '0',
            top: '40%',
            marginLeft: '16px',
            cursor: isTransitioning ? 'not-allowed' : 'pointer',
          }}
          alt={t('previous')}
          data-testid="previous-slide-arrow"
        />
        <Arrow
          alt={t('next')}
          data-testid="next-slide-arrow"
          onClick={onRightClick}
          style={{
            position: 'absolute',
            right: '0',
            top: '40%',
            marginRight: '16px',
            cursor: isTransitioning ? 'not-allowed' : 'pointer',
          }}
          direction={Directions.Right}
        />
      </div>
      <style>
        {`
          .react-multiple-carousel__arrow {
            display: none;
          }
        `}
      </style>
    </>
  );
}
